import TrackedVideo from "../components/TrackedVideo";
import { StationConfirmationModal } from './../utils/StationConfirmationModal';
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  buildVideoPathCandidates,
  normalizeBucketFilePath,
  resolveCanonicalClipPath,
  stripExtension
} from "./videoResolver";

const API_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_LOCAL_API_URL


export function StationView({
  station,
  state,
  rerender,
  setRerender,
  draggable = false,
  onDragStart,
  onDragOver,
  onDrop,
  index,
  dragLabel = "Dra",
  showDelete = true
}) {
  const [expanded, setExpanded] = useState(false);
  const [videoSrc, setVideoSrc] = useState("");
  const [isResolvingVideo, setIsResolvingVideo] = useState(false);
  const [resolvedCanonicalPath, setResolvedCanonicalPath] = useState("");
  const [candidateIndex, setCandidateIndex] = useState(0);
  const [noteDraft, setNoteDraft] = useState(station.userNote || "");
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [noteMessage, setNoteMessage] = useState({ type: "", text: "" });
  const selectedVideoPath = station.videoUrl || station.videoPath || "";
  const videoPathCandidates = useMemo(
    () => buildVideoPathCandidates(selectedVideoPath, resolvedCanonicalPath),
    [resolvedCanonicalPath, selectedVideoPath]
  );
 
  async function bucketRequest(queryPath) {
    const normalizedPath = normalizeBucketFilePath(queryPath);

    const response = await axios.get(`${API_URL}/bucket/presigned-url-file`, {
      headers: { 
        'Content-Type': 'application/json',
        'token': localStorage.getItem("token")
      },
      params: {
        file: encodeURIComponent(normalizedPath)
      }
    });

    return response.data;
  }

  function validateVideoUrl(url) {
    return new Promise((resolve) => {
      const probeVideo = document.createElement("video");

      const cleanup = () => {
        probeVideo.onloadedmetadata = null;
        probeVideo.onerror = null;
        probeVideo.removeAttribute("src");
        probeVideo.load();
      };

      probeVideo.preload = "metadata";
      probeVideo.onloadedmetadata = () => {
        cleanup();
        resolve(true);
      };
      probeVideo.onerror = () => {
        cleanup();
        resolve(false);
      };
      probeVideo.src = url;
    });
  }

  useEffect(() => {
    setVideoSrc("");
    setIsResolvingVideo(false);
    setResolvedCanonicalPath("");
    setCandidateIndex(0);
  }, [selectedVideoPath]);

  useEffect(() => {
    setNoteDraft(station.userNote || "");
    setNoteMessage({ type: "", text: "" });
  }, [station._id, station.userNote]);

  useEffect(() => {
    if (!expanded) {
      setVideoSrc("");
      setIsResolvingVideo(false);
      setCandidateIndex(0);
    }
  }, [expanded]);

  useEffect(() => {
    let cancelled = false;

    async function resolveCanonicalPath() {
      if (!expanded || resolvedCanonicalPath) {
        return;
      }
      const canonicalPath = await resolveCanonicalClipPath({
        selectedVideoPath,
        stationTitle: station.title || ""
      });

      if (!cancelled && canonicalPath) {
        setResolvedCanonicalPath(canonicalPath);
      }
    }

    async function resolveVideoSource() {
      if (!expanded || videoSrc || videoPathCandidates.length === 0 || !videoPathCandidates[candidateIndex]) {
        return;
      }

      setIsResolvingVideo(true);

      try {
        const candidate = videoPathCandidates[candidateIndex];
        const candidateUrl = await bucketRequest(candidate);
        const isPlayable = await validateVideoUrl(candidateUrl);

        if (cancelled) {
          return;
        }

        if (isPlayable) {
          setVideoSrc(candidateUrl);
          setIsResolvingVideo(false);
          return;
        }

        if (candidateIndex < videoPathCandidates.length - 1) {
          setCandidateIndex((currentIndex) => currentIndex + 1);
          return;
        }
      } catch (error) {
        if (cancelled) {
          return;
        }
      }

      if (!cancelled && candidateIndex >= videoPathCandidates.length - 1) {
        setVideoSrc("");
        setIsResolvingVideo(false);
      }
    }

    resolveCanonicalPath();
    resolveVideoSource();

    return () => {
      cancelled = true;
    };
  }, [candidateIndex, expanded, resolvedCanonicalPath, selectedVideoPath, station.title, videoPathCandidates, videoSrc]);

  async function saveNote() {
    try {
      setIsSavingNote(true);
      const response = await axios.patch(`${API_URL}/training/station-note`, {
        sessionId: state.session,
        stationId: station._id,
        note: noteDraft
      }, {
        headers: {
          "Content-Type": "application/json",
          token: localStorage.getItem("token")
        }
      });

      station.userNote = response.data.note || "";
      station.userNoteUpdatedAt = response.data.updatedAt || null;
      setNoteDraft(response.data.note || "");
      setNoteMessage({
        type: "success",
        text: response.data.note ? "Din anteckning sparades." : "Din anteckning togs bort."
      });
      if (typeof setRerender === "function") {
        setRerender((previous) => !previous);
      }
    } catch (error) {
      setNoteMessage({
        type: "error",
        text: error.response?.data?.message || "Kunde inte spara anteckningen."
      });
    } finally {
      setIsSavingNote(false);
    }
  }

  return <div
            draggable={draggable}
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDrop={onDrop}
            className="my-3 rounded-[24px] border border-sky-200/80 bg-gradient-to-r from-white via-sky-50 to-cyan-50 px-4 py-4 shadow-sm"
          >
            <div className="flex justify-between">
                <button className="h-8 w-full truncate text-left font-semibold text-slate-800" onClick={() => setExpanded(!expanded)}>
                  {typeof index === "number" ? `${index + 1}. ` : ""}Titel: {stripExtension(station.title)}
                </button>
                <div className="ml-3 flex items-center gap-2">
                  {draggable && <span className="rounded-full border border-sky-200 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-sky-700">{dragLabel}</span>}
                  {showDelete && <StationConfirmationModal stationId={station._id} state={state} rerender={rerender} setRerender={setRerender} />}
                </div>
            </div>

            {expanded && <div className="flex flex-col">
                <div className="mt-2 text-sm text-slate-600">{station.description}</div>
                {isResolvingVideo && !videoSrc && <div className="mt-3 text-sm text-slate-500">Laddar videoförhandsvisning...</div>}
                {videoSrc && <TrackedVideo clipPath={resolvedCanonicalPath || videoPathCandidates[candidateIndex]} clipTitle={stripExtension(station.title)} sessionId={state?.session} controls key={`${station.title}-${candidateIndex}`} className="mt-3 max-w-full rounded-2xl shadow-sm"><source src={videoSrc} /></TrackedVideo>}

                <div className="mt-4 rounded-[20px] border border-slate-200 bg-white/80 p-4">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <p className="text-sm font-semibold text-slate-800">Mina anteckningar</p>
                      <p className="mt-1 text-sm text-slate-500">Sparas bara för dig i det här passet.</p>
                    </div>
                    <button
                      className="rounded-[18px] border border-sky-200 bg-sky-50 px-4 py-2 text-sm font-semibold text-sky-700 shadow-sm transition-colors hover:bg-sky-100 disabled:cursor-not-allowed disabled:opacity-60"
                      onClick={saveNote}
                      disabled={isSavingNote || noteDraft === (station.userNote || "")}
                    >
                      {isSavingNote ? "Sparar..." : "Spara anteckning"}
                    </button>
                  </div>
                  <textarea
                    value={noteDraft}
                    className="mt-3 min-h-[96px] w-full rounded-2xl border border-sky-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm outline-none transition-colors focus:border-sky-400"
                    placeholder="Lägg till egen notering här"
                    onChange={(event) => setNoteDraft(event.target.value)}
                  />
                  {station.userNoteUpdatedAt && (
                    <p className="mt-2 text-xs text-slate-500">
                      Senast sparad: {new Intl.DateTimeFormat("sv-SE", {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit"
                      }).format(new Date(station.userNoteUpdatedAt))}
                    </p>
                  )}
                  {noteMessage.text && (
                    <p className={`mt-2 text-sm ${noteMessage.type === "error" ? "text-red-700" : "text-green-700"}`}>
                      {noteMessage.text}
                    </p>
                  )}
                </div>
              </div>}
        </div>;
}
  
